import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import menuFormatExecutionService from '../../services/menuFormatExecutionService';
import InventoryRecipeEditor from './InventoryRecipeEditor';
import FormatLinkPanel from './FormatLinkPanel';
import { formatQty } from '../../utils/quantityFormat';
import type { InventoryItemWithBalance } from '../../services/inventoryItemService';
import type {
  ExecutionStatus,
  InventoryItemRecipeComponent,
  MenuFormatExecutionSummary,
} from '../../types/execution';

/**
 * Phase 7.A.2a/2b/2c — "Inventory execution" block for one serving option.
 *
 * Renders the linked execution item, its behavior, status, derived availability
 * and (for recipe items) the component list + inline recipe editor (2b). When
 * the serving option is UNLINKED, the operator can link an existing item or
 * create a RECIPE_CONSUMPTION execution item (2c).
 *
 * The summary endpoint may be absent on an environment where the backend Phase
 * 7.A is not deployed: a 404 surfaces a soft "unavailable" line instead of
 * breaking the whole menu item form.
 */

interface FormatExecutionBlockProps {
  venueId: string;
  menuId: string;
  itemId: string;
  formatId: string;
  /** Menu item kind — drives kind-aware copy in the UNLINKED state (e.g. food
   *  inventory linking is optional). 'DISH' softens the unlinked wording. */
  itemKind?: string;
  /** Pre-filled name for a created execution item, e.g. "Negroni — Glass". */
  suggestedName?: string;
  /** Keeps the parent menu-item form's row state in sync after a link/create,
   *  so its diff-save never overwrites inventoryItemId with a stale value. */
  onLinkedItemChange?: (formatRowId: string, item: InventoryItemWithBalance | null) => void;
}

const STATUS_LABEL: Record<ExecutionStatus, string> = {
  UNLINKED: 'Not linked',
  LINKED_DIRECT_STOCK: 'Sold from stock',
  LINKED_RECIPE_NO_RECIPE: 'Recipe needed',
  LINKED_RECIPE_READY: 'Ready',
};

/** "150 ml (= 0.2)" when the operator input snapshot exists, else canonical qty. */
function componentLine(c: InventoryItemRecipeComponent): string {
  const name = c.displayName ?? 'Inventory item';
  if (c.inputQuantity != null && c.inputUnit) {
    return `${name} — ${formatQty(c.inputQuantity)} ${c.inputUnit} (= ${formatQty(c.quantity)})`;
  }
  return `${name} — ${formatQty(c.quantity)}`;
}

function availabilityText(summary: MenuFormatExecutionSummary): string {
  const { value, basis } = summary.derivedAvailability;
  if (value == null) return '—';
  // Direct stock reports a raw balance; recipe items report whole servings.
  if (basis === 'DIRECT_BALANCE') return `${formatQty(value)} available`;
  return `≈ ${formatQty(value)} servings`;
}

export default function FormatExecutionBlock({
  venueId,
  menuId,
  itemId,
  formatId,
  itemKind,
  suggestedName,
  onLinkedItemChange,
}: FormatExecutionBlockProps) {
  const [summary, setSummary] = useState<MenuFormatExecutionSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const loadSummary = useCallback(async (): Promise<void> => {
    if (!venueId || !menuId || !itemId || !formatId) return;
    setLoading(true);
    setError(null);
    setUnavailable(false);
    try {
      const data = await menuFormatExecutionService.getFormatExecution(venueId, menuId, itemId, formatId);
      setSummary(data);
    } catch (err: any) {
      // 404 = endpoint not present (backend Phase 7.A not deployed) OR this
      // format/item not found. Either way, degrade softly.
      if (err?.response?.status === 404) {
        setUnavailable(true);
      } else {
        setError('Could not load stock & recipe details.');
      }
    } finally {
      setLoading(false);
    }
  }, [venueId, menuId, itemId, formatId]);

  useEffect(() => {
    let active = true;
    if (active) void loadSummary();
    return () => {
      active = false;
    };
  }, [loadSummary]);

  const handleSaved = useCallback(() => {
    setEditing(false);
    void loadSummary();
  }, [loadSummary]);

  // 7.A.2c — after a link/create: sync parent row state (so the form's main
  // Save won't clobber the link), optionally open the recipe editor (for a
  // freshly-created RECIPE_CONSUMPTION item), then reload the summary.
  const handleLinked = useCallback(
    (item: InventoryItemWithBalance, autoOpenRecipe: boolean) => {
      onLinkedItemChange?.(formatId, item);
      if (autoOpenRecipe) setEditing(true);
      void loadSummary();
    },
    [onLinkedItemChange, formatId, loadSummary],
  );

  const header = (
    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
      Stock &amp; recipe
    </Typography>
  );

  const wrapper = (children: React.ReactNode) => (
    <Box
      sx={{
        mt: 1.5,
        p: 1.5,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'action.hover',
      }}
    >
      {header}
      <Box sx={{ mt: 1 }}>{children}</Box>
    </Box>
  );

  if (loading) {
    return wrapper(
      <Stack direction="row" spacing={1} alignItems="center">
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Loading…
        </Typography>
      </Stack>,
    );
  }

  if (unavailable) {
    return wrapper(
      <Typography variant="body2" color="text.secondary">
        Stock &amp; recipe details are not available yet.
      </Typography>,
    );
  }

  if (error) {
    return wrapper(
      <Alert severity="warning" variant="outlined" sx={{ py: 0 }}>
        {error}
      </Alert>,
    );
  }

  if (!summary) return null;

  const behavior = summary.executionBehavior;
  const isRecipe = behavior === 'RECIPE_CONSUMPTION';
  const recipeComponents = summary.recipe?.components ?? [];

  return wrapper(
    <Stack spacing={1}>
      {/* Linked item + behavior chip */}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2">
          <Box component="span" color="text.secondary">
            Linked stock item:{' '}
          </Box>
          {summary.linkedInventoryItem ? summary.linkedInventoryItem.displayName : 'Not linked'}
        </Typography>
        {behavior && (
          <Chip
            size="small"
            label={isRecipe ? 'Made from a recipe' : 'Sold from stock'}
            color={isRecipe ? 'secondary' : 'info'}
            variant="outlined"
          />
        )}
      </Stack>

      {/* Status */}
      <Typography variant="caption" color="text.secondary">
        Status: {STATUS_LABEL[summary.executionStatus]}
      </Typography>

      {/* Estimated availability */}
      <Typography variant="body2">
        <Box component="span" color="text.secondary">
          Estimated availability:{' '}
        </Box>
        {availabilityText(summary)}
      </Typography>

      {/* UNLINKED → link existing / create recipe item (7.A.2c) */}
      {summary.executionStatus === 'UNLINKED' && (
        <FormatLinkPanel
          venueId={venueId}
          itemId={itemId}
          formatId={formatId}
          itemKind={itemKind}
          suggestedName={suggestedName}
          onLinked={handleLinked}
        />
      )}

      {/* No-recipe warning + add action (RECIPE_CONSUMPTION, no recipe) */}
      {summary.executionStatus === 'LINKED_RECIPE_NO_RECIPE' && !editing && (
        <Box>
          <Alert severity="warning" variant="outlined" sx={{ py: 0 }}>
            No recipe yet — add what one serving depletes to compute availability.
          </Alert>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setEditing(true)}
            sx={{ textTransform: 'none', mt: 1 }}
          >
            Add serving recipe
          </Button>
        </Box>
      )}

      {/* Recipe components (read-only) + edit action */}
      {isRecipe && recipeComponents.length > 0 && !editing && (
        <Box>
          <Divider sx={{ my: 0.5 }} />
          <Typography variant="caption" color="text.secondary">
            What one serving depletes
          </Typography>
          <Stack component="ul" sx={{ pl: 2.5, my: 0.5 }} spacing={0.25}>
            {recipeComponents.map((c) => (
              <Typography
                key={c.inventoryItemId}
                component="li"
                variant="body2"
              >
                {componentLine(c)}
              </Typography>
            ))}
          </Stack>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setEditing(true)}
            sx={{ textTransform: 'none', mt: 0.5 }}
          >
            Edit serving recipe
          </Button>
        </Box>
      )}

      {/* Inline recipe editor (RECIPE_CONSUMPTION only) */}
      {isRecipe && summary.linkedInventoryItem && (
        <Collapse in={editing} unmountOnExit>
          <Divider sx={{ my: 0.5 }} />
          <InventoryRecipeEditor
            venueId={venueId}
            itemId={summary.linkedInventoryItem._id}
            initialComponents={summary.recipe?.components ?? null}
            onSaved={handleSaved}
            onCancel={() => setEditing(false)}
          />
        </Collapse>
      )}

      {/* Standing setup-only note */}
      <Typography variant="caption" color="text.disabled">
        Not yet active for live POS sales — used for setup and availability.
      </Typography>
    </Stack>,
  );
}
